import network from '@/services/network';
import { registriereFeldMeta } from '@/views/home/customer/application/felder';

/**
 * Personenfeld-Konfiguration pro Gesellschaft × Antragstyp × Rolle (BER-67).
 * Rolle = Versicherungsnehmer (vn) oder Versicherte Person (vp).
 *
 * `resolve` liefert dem Wizard die effektiven Felder (Standard + konfigurierte
 * Zusätze) je Rolle. Die übrigen Endpunkte sind die Admin-Verwaltung
 * (`application-vp-fields.manage`).
 */
export type PersonRole = 'vn' | 'vp';

export interface VpFieldOption {
  value: string;
  label: string;
  /** Nur softfair-Katalogfelder (sf_…): Eingabetyp fürs Wizard-Rendering. */
  typ?: 'text' | 'datum' | 'select' | 'janein';
  /** Nur bei typ=select: Auswahloptionen (API-Enum + lesbares Label). */
  optionen?: { value: string; label: string }[];
  /**
   * Nur in der Wizard-Antwort (`resolve`): Feld ist KEINE Pflicht (Admin-
   * Schieberegler „Optional", BER-67 01.10.2026).
   */
  optional?: boolean;
}

export interface VpFieldConfig {
  id: number;
  provider_value: string;
  application_type_value: string;
  role: PersonRole;
  fields: string[];
  /** Felder ohne Pflicht im Wizard; null = Vorgabe (`default_optional`). */
  optional_fields: string[] | null;
}

/** Feld-Katalog (Standard + optional) für eine Rolle. */
export interface VpFieldRoleCatalog {
  standard_fields: VpFieldOption[];
  optional_fields: VpFieldOption[];
}

export interface VpFieldAdminData {
  // Kataloge je Rolle: { vn: {…}, vp: {…} }.
  catalog: Record<PersonRole, VpFieldRoleCatalog>;
  configs: VpFieldConfig[];
  /** Vorbelegung „Optional" für Kombinationen ohne gespeicherte Auswahl. */
  default_optional: string[];
}

/**
 * Effektive Felder für eine (Gesellschaft, Antragstyp, Rolle)-Kombination.
 * `role` optional (Default vp) für Rückwärtskompatibilität.
 */
const resolve = async (
  providerValue: string,
  applicationTypeValue: string,
  role: PersonRole = 'vp',
): Promise<VpFieldOption[]> => {
  const { data } = await network.get<{ data: { fields: VpFieldOption[] } }>(
    '/application-vp-fields/resolve',
    {
      params: {
        provider_value: providerValue,
        application_type_value: applicationTypeValue,
        role,
      },
    },
  );
  // Server-Meta der dynamischen softfair-Felder für das Rendering merken.
  registriereFeldMeta(data.data.fields);
  return data.data.fields;
};

const getAdminData = async (): Promise<VpFieldAdminData> => {
  const { data } = await network.get<{ data: VpFieldAdminData }>('/application-vp-fields');
  return data.data;
};

const upsert = async (input: {
  provider_value: string;
  application_type_value: string;
  role: PersonRole;
  fields: string[];
  optional_fields: string[];
}): Promise<VpFieldConfig> => {
  const { data } = await network.post<{ data: VpFieldConfig }>('/application-vp-fields', input);
  return data.data;
};


const applicationVpFields = { resolve, getAdminData, upsert };

export default applicationVpFields;
